// Shared helper for the "Gérer les affectations / Assigner" modals in
// Frais & Relances (frais annexes, remises, échéanciers). Groups belong to
// individual filière ids: a parent owns its early-year groups while each
// option owns its specialisation-year groups. This loads groups across the
// whole filière family (parent + options) and appends the option name to
// every group that comes from an option, so "3ème Année - Groupe A" is no
// longer ambiguous between two options.

import { getGroupes } from '@/services/academicManagementService';

export interface AssignGroupOption {
  value: string;
  label: string;
  niveau?: number;
}

export interface FamilyOptionInput {
  id: string;
  optionName: string | null;
  filiere: string;
}

interface LoadFamilyGroupsParams {
  parentFiliereId: string;
  options: FamilyOptionInput[];
  selectedOptionId: string; // '' when no specific option is chosen
  academicYearId: string;
  campusId?: string;
  niveau?: number | '';
}

/**
 * Fetch and merge the groups of a filière family, disambiguating option groups
 * with the option name. When a specific option is selected only its groups are
 * returned; otherwise the parent's groups plus every option's groups are
 * merged. Optionally filtered to a single niveau.
 */
export async function loadFamilyGroups(
  params: LoadFamilyGroupsParams,
): Promise<AssignGroupOption[]> {
  const { parentFiliereId, options, selectedOptionId, academicYearId, campusId, niveau } = params;

  const optionLabel = (o: FamilyOptionInput): string => o.optionName || o.filiere;

  // The filière ids to pull groups from, each tagged with the option label to
  // append (null for the parent's own groups).
  const sources: Array<{ id: string; optionName: string | null }> = selectedOptionId
    ? [
        {
          id: selectedOptionId,
          optionName: (() => {
            const opt = options.find((o) => o.id === selectedOptionId);
            return opt ? optionLabel(opt) : null;
          })(),
        },
      ]
    : [
        { id: parentFiliereId, optionName: null },
        ...options.map((o) => ({ id: o.id, optionName: optionLabel(o) })),
      ];

  const results = await Promise.all(
    sources.map((s) =>
      getGroupes(s.id, academicYearId, campusId)
        .then((gs) => gs.map((g) => ({ g: g as AssignGroupOption, optionName: s.optionName })))
        .catch(() => [] as Array<{ g: AssignGroupOption; optionName: string | null }>),
    ),
  );

  const seen = new Set<string>();
  const merged: AssignGroupOption[] = [];
  for (const list of results) {
    for (const { g, optionName } of list) {
      if (seen.has(g.value)) continue;
      seen.add(g.value);
      merged.push(optionName ? { ...g, label: `${g.label} — ${optionName}` } : g);
    }
  }

  return niveau ? merged.filter((g) => g.niveau === niveau) : merged;
}
